/** Pure date/layout math for the timeline board. No DOM, no React. */
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";

/** Half-open [start, end) span of ISO dates — same convention as stays. */
export interface Span {
  start: string;
  end: string;
}

export function addDaysISO(iso: string, days: number): string {
  return format(addDays(parseISO(iso), days), "yyyy-MM-dd");
}

export function diffDaysISO(from: string, to: string): number {
  return differenceInCalendarDays(parseISO(to), parseISO(from));
}

export function buildDays(startISO: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addDaysISO(startISO, i));
}

export function spansOverlap(a: Span, b: Span): boolean {
  return a.start < b.end && b.start < a.end;
}

/**
 * Clip a span to the visible window [windowStart, windowStart + windowDays).
 * Returns the column offset/width in day units, or null when out of view.
 */
export function clipToWindow(
  span: Span,
  windowStart: string,
  windowDays: number
): { startIndex: number; nights: number; clippedStart: boolean; clippedEnd: boolean } | null {
  const windowEnd = addDaysISO(windowStart, windowDays);
  if (!spansOverlap(span, { start: windowStart, end: windowEnd })) return null;
  const startIndex = Math.max(0, diffDaysISO(windowStart, span.start));
  const endIndex = Math.min(windowDays, diffDaysISO(windowStart, span.end));
  return {
    startIndex,
    nights: endIndex - startIndex,
    clippedStart: span.start < windowStart,
    clippedEnd: span.end > windowEnd,
  };
}

/**
 * Greedy lane packing for overlapping spans: earlier-starting spans first,
 * each takes the lowest lane whose last span has ended.
 */
export function assignLanes<T extends Span>(items: T[]): { item: T; lane: number }[] {
  const sorted = [...items].sort((a, b) =>
    a.start < b.start ? -1 : a.start > b.start ? 1 : a.end < b.end ? -1 : 1
  );
  const laneEnds: string[] = [];
  return sorted.map((item) => {
    let lane = laneEnds.findIndex((end) => end <= item.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.end);
    } else {
      laneEnds[lane] = item.end;
    }
    return { item, lane };
  });
}

export function laneCount(lanes: { lane: number }[]): number {
  return lanes.length === 0 ? 1 : Math.max(...lanes.map((l) => l.lane)) + 1;
}
